// watch/UpNextPanel.tsx — "Up next" (1.27.50, owner concept): the Autoplay switch and three
// tabs — For you, From <creator>, Related — over the list. The page owns the tab and the list
// (autoplay plays the first video of whichever tab is showing), this only draws them.
// 1.27.60: your queue (⋯ Add to queue) sits above the tabs and plays first.
import { useMemo } from 'react';
import styled from 'styled-components';
import { IconButton, MetaRow, Panel, Switch } from '@/ui';
import type { QueuedVideo } from '../../queue/videoQueue';
import { UpNextRowMenu } from './UpNextRowMenu';
import type { Post } from '@/core/models/post';
import type { UpNextMode } from '@/core/services/videoFeedService';
import { RailVideoItem } from '../../RailVideoItem';
import { toCards } from '../realItems';

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

// && — the shell's RailGroup sizes every rail h2 down to a panel label; this one is the
// rail's title (concept), so it keeps the section-heading size.
const Title = styled.h2`
  && {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AutoplayLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: 500 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  overflow-x: auto;
`;

const Tab = styled.button<{ $on: boolean }>`
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.onAccent : theme.color.textPrimary)};
  font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  cursor: pointer;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    filter: brightness(1.12);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Queue = styled.section`
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  h3 {
    margin: 0;
    font: 700 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
  }
  header button {
    padding: 0;
    border: 0;
    background: none;
    color: ${({ theme }) => theme.color.accentCyan};
    font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    cursor: pointer;
  }
  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 4px;
  }
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 2px;
`;

const Note = styled.p`
  margin: 0;
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface UpNextPanelProps {
  creatorName: string;
  mode: UpNextMode;
  onMode: (mode: UpNextMode) => void;
  posts: Post[] | null;
  autoplay: boolean;
  onAutoplay: (next: boolean) => void;
  /** Your queue, current video excluded. */
  queue?: QueuedVideo[];
  onRemoveQueued?: (id: string) => void;
  onClearQueue?: () => void;
}

export function UpNextPanel({
  creatorName,
  mode,
  onMode,
  posts,
  autoplay,
  onAutoplay,
  queue = [],
  onRemoveQueued,
  onClearQueue,
}: UpNextPanelProps) {
  const { items, creators } = useMemo(() => toCards(posts ?? []), [posts]);
  const byId = useMemo(() => new Map((posts ?? []).map((p) => [p.id, p])), [posts]);
  const tabs: { id: UpNextMode; label: string }[] = [
    { id: 'for_you', label: 'For you' },
    { id: 'creator', label: `From ${creatorName}` },
    { id: 'related', label: 'Related' },
  ];
  return (
    <nav aria-label="Up next">
      <Panel>
        <Head>
          <Title>Up next</Title>
          <AutoplayLabel>
            Autoplay
            <Switch checked={autoplay} onChange={onAutoplay} label="Autoplay the next video" />
          </AutoplayLabel>
        </Head>
        {queue.length > 0 && (
          <Queue aria-labelledby="up-next-queue">
            <header>
              <h3
                id="up-next-queue"
                aria-label={`Your queue, ${queue.length} ${queue.length === 1 ? 'video' : 'videos'}`}
              >
                <MetaRow>
                  <span>Your queue</span>
                  <span>{queue.length}</span>
                </MetaRow>
              </h3>
              <button type="button" onClick={onClearQueue}>
                Clear
              </button>
            </header>
            <List>
              {queue.map((q) => (
                <li key={q.id}>
                  <RailVideoItem video={q.item} creator={q.creator} />
                  <IconButton
                    icon="close"
                    size={16}
                    label={`Remove ${q.item.title} from your queue`}
                    onClick={() => onRemoveQueued?.(q.id)}
                  />
                </li>
              ))}
            </List>
          </Queue>
        )}
        <Tabs role="tablist" aria-label="Up next lists">
          {tabs.map((t) => (
            <Tab
              key={t.id}
              type="button"
              role="tab"
              aria-selected={mode === t.id}
              $on={mode === t.id}
              onClick={() => onMode(t.id)}
            >
              {t.label}
            </Tab>
          ))}
        </Tabs>
        <div role="tabpanel" aria-label={tabs.find((t) => t.id === mode)?.label}>
          {posts === null ? (
            <Note role="status">Loading…</Note>
          ) : items.length === 0 ? (
            <Note>
              {mode === 'creator'
                ? `No other videos from ${creatorName} yet.`
                : 'No more videos here yet.'}
            </Note>
          ) : (
            <List>
              {items.map((v) => (
                <Row key={v.id}>
                  <RailVideoItem video={v} creator={creators[v.authorId]} />
                  <UpNextRowMenu post={byId.get(v.id)!} title={v.title} />
                </Row>
              ))}
            </List>
          )}
        </div>
      </Panel>
    </nav>
  );
}
